/**
 * WhatsApp Settings (admin)
 * Configure the WhatsApp AI-bot module: Meta Cloud API credentials, the bot
 * toggles, the knowledge base the bot is grounded in, and the sales-alert
 * template.
 *
 * - Access token and app secret are stored ENCRYPTED on the server; only the
 *   last 4 chars come back. Leave those fields blank to keep the existing value.
 * - `enabled` is the master switch (webhook still ACKs Meta when off).
 * - `botEnabled` decides whether the AI auto-replies; turn it off for a pure
 *   human inbox while still logging every message.
 */

import React, { useEffect, useState } from 'react';
import {
  Card, Switch, Input, InputNumber, Button, Space, Typography, Tag, Spin, Alert,
  Select, Divider, message,
} from 'antd';
import {
  SaveOutlined, ReloadOutlined, WhatsAppOutlined, RobotOutlined,
} from '@ant-design/icons';
import { getWhatsAppConfig, updateWhatsAppConfig } from '../api';

const { Title, Text, Paragraph } = Typography;

const KB_PLACEHOLDER = `# Auriksha — facts the bot may use

## Services
- ...

## Cities served
- Durgapur, ...

## What the bot must NOT do
- Never quote a price. Say "let me check with the team".
- Never guarantee rankings or results.`;

export default function WhatsAppSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Secrets are write-only from here; blank means "keep existing".
  const [accessToken, setAccessToken] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [messageApi, contextHolder] = message.useMessage();

  const load = async () => {
    setLoading(true);
    try {
      const res = await getWhatsAppConfig();
      // Backend wraps payloads as { success, data }.
      setCfg(res.data?.data || null);
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to load WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const patch = (changes) => setCfg((prev) => ({ ...prev, ...changes }));

  const handleSave = async () => {
    if (cfg.enabled && (!cfg.phoneNumberId || (!cfg.accessTokenConfigured && !accessToken.trim()))) {
      return messageApi.error('Add the Phone Number ID and access token before enabling the module');
    }
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        botEnabled: cfg.botEnabled,
        phoneNumberId: cfg.phoneNumberId,
        businessAccountId: cfg.businessAccountId,
        graphApiVersion: cfg.graphApiVersion,
        verifyToken: cfg.verifyToken,
        salesAlertNumbers: cfg.salesAlertNumbers || [],
        salesAlertTemplateName: cfg.salesAlertTemplateName,
        salesAlertTemplateLang: cfg.salesAlertTemplateLang,
        muteHours: cfg.muteHours,
        knowledgeBase: cfg.knowledgeBase || '',
      };
      if (accessToken.trim()) payload.accessToken = accessToken.trim();
      if (appSecret.trim()) payload.appSecret = appSecret.trim();
      const res = await updateWhatsAppConfig(payload);
      setCfg(res.data?.data || cfg);
      setAccessToken('');
      setAppSecret('');
      messageApi.success('WhatsApp settings saved');
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  const webhookHint = '/api/v1/whatsapp/webhook'; // append to your public backend URL

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      {contextHolder}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            <WhatsAppOutlined style={{ color: '#25D366' }} /> WhatsApp Bot
          </Title>
          <Text type="secondary">
            Answer inbound WhatsApp queries with the AI assistant, grounded in the knowledge base
            below, and push qualified leads to your sales team.
          </Text>
        </div>

        {/* Master toggles */}
        <Card size="small">
          <Space size="large" wrap>
            <Space>
              <Switch checked={cfg.enabled} onChange={(v) => patch({ enabled: v })} />
              <div>
                <Text strong>Module enabled</Text><br />
                <Text type="secondary">Process inbound webhooks. Off = messages ignored.</Text>
              </div>
            </Space>
            <Space>
              <Switch checked={cfg.botEnabled} onChange={(v) => patch({ botEnabled: v })} />
              <div>
                <Text strong><RobotOutlined /> AI auto-reply</Text><br />
                <Text type="secondary">Off = human inbox only (still logs everything).</Text>
              </div>
            </Space>
          </Space>
          {cfg.lastError && (
            <Alert style={{ marginTop: 12 }} type="warning" showIcon
              message="Last error" description={cfg.lastError} />
          )}
        </Card>

        {/* Meta Cloud API */}
        <Card title="Meta Cloud API" size="small">
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            From your Meta app → WhatsApp → API Setup. Point Meta&apos;s webhook callback at{' '}
            <Text code>{webhookHint}</Text> on your public backend URL, and subscribe to
            {' '}<Text code>messages</Text>, <Text code>smb_message_echoes</Text> and{' '}
            <Text code>account_update</Text>.
          </Paragraph>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>Phone Number ID</Text>
              <Input value={cfg.phoneNumberId || ''} placeholder="WA_PHONE_NUMBER_ID"
                onChange={(e) => patch({ phoneNumberId: e.target.value })} />
            </div>
            <div>
              <Text strong>Business Account ID</Text>
              <Input value={cfg.businessAccountId || ''} placeholder="WA_BUSINESS_ACCOUNT_ID"
                onChange={(e) => patch({ businessAccountId: e.target.value })} />
            </div>
            <Space size="large" wrap style={{ width: '100%' }}>
              <div>
                <Text strong>Graph API version</Text><br />
                <Input value={cfg.graphApiVersion || ''} style={{ width: 120 }}
                  onChange={(e) => patch({ graphApiVersion: e.target.value })} />
              </div>
              <div>
                <Text strong>Webhook verify token</Text><br />
                <Input value={cfg.verifyToken || ''} placeholder="a secret you choose"
                  style={{ width: 260 }}
                  onChange={(e) => patch({ verifyToken: e.target.value })} />
              </div>
            </Space>

            <Divider style={{ margin: '4px 0' }} />

            <div>
              <Text strong>Access token</Text>{' '}
              {cfg.accessTokenConfigured
                ? <Tag color="green">•••• {cfg.accessTokenHint}</Tag>
                : <Tag color="orange">Not configured</Tag>}
              <Input.Password autoComplete="off" value={accessToken}
                placeholder={cfg.accessTokenConfigured ? 'Leave blank to keep, or paste to rotate' : 'Permanent system-user token'}
                onChange={(e) => setAccessToken(e.target.value)} />
            </div>
            <div>
              <Text strong>App secret</Text>{' '}
              {cfg.appSecretConfigured
                ? <Tag color="green">•••• {cfg.appSecretHint}</Tag>
                : <Tag color="orange">Not configured</Tag>}
              <Input.Password autoComplete="off" value={appSecret}
                placeholder={cfg.appSecretConfigured ? 'Leave blank to keep, or paste to rotate' : 'Verifies webhook signatures (Settings → Basic)'}
                onChange={(e) => setAppSecret(e.target.value)} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Used to validate Meta&apos;s webhook signature. Without it, signatures are not verified.
              </Text>
            </div>
          </Space>
        </Card>

        {/* Sales alerts */}
        <Card title="Sales alerts" size="small">
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            When a qualified lead lands or a customer asks for a human, these numbers get the
            approved <Text code>{cfg.salesAlertTemplateName || 'lead_alert'}</Text> template
            (sales are almost always outside the 24-hour window, so plain text would fail).
          </Paragraph>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>Sales numbers (E.164, no +)</Text>
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="919812345678"
                value={cfg.salesAlertNumbers || []}
                onChange={(v) => patch({ salesAlertNumbers: v })}
                tokenSeparators={[',', ' ']}
              />
            </div>
            <Space size="large" wrap>
              <div>
                <Text strong>Template name</Text><br />
                <Input value={cfg.salesAlertTemplateName || ''} style={{ width: 200 }}
                  onChange={(e) => patch({ salesAlertTemplateName: e.target.value })} />
              </div>
              <div>
                <Text strong>Template language</Text><br />
                <Input value={cfg.salesAlertTemplateLang || ''} style={{ width: 120 }}
                  onChange={(e) => patch({ salesAlertTemplateLang: e.target.value })} />
              </div>
              <div>
                <Text strong>Bot mute after handoff (hours)</Text><br />
                <InputNumber min={0} max={168} value={cfg.muteHours}
                  onChange={(v) => patch({ muteHours: v })} style={{ width: 120 }} />
              </div>
            </Space>
          </Space>
        </Card>

        {/* Knowledge base */}
        <Card
          title="Knowledge base"
          size="small"
          extra={<Text type="secondary">{cfg.knowledgeBaseChars ?? (cfg.knowledgeBase || '').length} chars</Text>}
        >
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            The entire factual universe the bot may draw on. Anything not written here, the bot is
            told to defer to the team — a bot that invents a price costs a client.
          </Paragraph>
          <Input.TextArea
            value={cfg.knowledgeBase || ''}
            onChange={(e) => patch({ knowledgeBase: e.target.value })}
            autoSize={{ minRows: 10, maxRows: 30 }}
            placeholder={KB_PLACEHOLDER}
            style={{ fontFamily: 'monospace', fontSize: 13 }}
          />
        </Card>

        <Space>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            Save Settings
          </Button>
          <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
        </Space>
      </Space>
    </div>
  );
}
